import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormBuilder, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateService, isArray } from '@ngx-translate/core';
import { FirestoreUser, IFirestoreInviteUser, NetworkConstants, UserRoles} from 'diwimamodelweb/lib';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { UserForm } from '../model/form';
import { PersonService } from '../person.service';
import { UserFeedbackService } from '../user-feedback.service';
import { buildCustomDiff, removeUndefined, deepCopy } from 'diwimamodelweb/lib/utils/obejct-diff';
import { SignaturePadDialogComponent } from '../signature-pad-dialog/signature-pad-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SettingsService } from '../settings.service';
import { UserService } from '../user.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { HuntingDistrictService } from '../hunting-district.service';
import { ContextNameLookupService } from '../context-name-lookup.service';
import { resolveNotificationContextPrefs } from '../utils/utils';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { UserNameLookupService } from 'src/app/cockpit/work-orders/user-name-lookup.service';

interface NotificationEventDef {
  key: string;
  labelKey: string;
}

/** One row in the "Kontext-Rollen" section — independent, always-live select, no shared/active state. */
interface ContextRoleRow {
  id: string;
  name: string;
  isMember: boolean;
  roles: string[];
}

const NOTIFICATION_EVENTS: NotificationEventDef[] = [
  { key: 'context-news-gameRecord', labelKey: 'FORM.userForm.notification_event.context-news-gameRecord' },
  { key: 'user-new-work-order',     labelKey: 'FORM.userForm.notification_event.user-new-work-order' },
];

const FIXED_ROLE_TO_OPTION_ID: Record<string, string> = {
  [UserRoles.ANSTELLER]: 'roleAnsteller',
  [UserRoles.JAGDLEITER]: 'roleJagdleiter',
  [UserRoles.USER]: 'roleUser',
  [UserRoles.HUNTER]: 'roleHunter',
  [UserRoles.REVIERLEITER]: 'roleRevierLeiter',
  [UserRoles.RECORDER]: 'roleRecorder',
  [UserRoles.APPVET]: 'roleAppVet',
  [UserRoles.VETHDLEADER] : 'roleVetHDLeader',
  [UserRoles.ADMIN]: 'roleAdmin',
  [UserRoles.CADAVERSEEK]: 'roleCadaverSeek',
  [UserRoles.CADAVERBT]: 'roleCadaverBT',
  [UserRoles.STAFF]: 'roleStaff',
  [UserRoles.SENIOR]: 'roleSenior'
}

const mapRawRolesToOptionIds = (roles: string[] | undefined): string[] =>
  (roles ?? []).map(r => FIXED_ROLE_TO_OPTION_ID[r] ?? r)

const buildRoles = (_roles: any): string[] => {
  let roles: string[] = []
  if (_roles.roleAnsteller) roles.push(UserRoles.ANSTELLER)
  if (_roles.roleJagdleiter) roles.push(UserRoles.JAGDLEITER)
  if (_roles.roleUser) roles.push(UserRoles.USER)
  if (_roles.roleHunter) roles.push(UserRoles.HUNTER)
  if (_roles.roleRevierLeiter) roles.push(UserRoles.REVIERLEITER)
  if (_roles.roleRecorder) roles.push(UserRoles.RECORDER)
  if (_roles.roleAppVet) roles.push(UserRoles.APPVET)
  if (_roles.roleVetHDLeader) roles.push(UserRoles.VETHDLEADER)
  if (_roles.roleAdmin) roles.push(UserRoles.ADMIN)
  if (_roles.roleCadaverSeek) roles.push(UserRoles.CADAVERSEEK)
  if (_roles.roleCadaverBT) roles.push(UserRoles.CADAVERBT)
  if (_roles.roleStaff) roles.push(UserRoles.STAFF)
  if (_roles.roleSenior) roles.push(UserRoles.SENIOR)
  return roles
}

const buildUser = (oldUserData: FirestoreUser|null, formValue: any): [FirestoreUser|null, FirestoreUser] => {
  const oldUser: any = Object.assign({}, oldUserData ? oldUserData : {})
    const {
      displayName, roleAnsteller, 
      roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder,
      roleAppVet, roleVetHDLeader, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior,
      firstName,
        lastName,
        gender,
        salutation,
        title,
        address1,
        address2,
        zip,
        city,
        country,
        countryCode,
        signature,
        telephone,
        contextIds, contextMembership
    } = formValue
    const roles = buildRoles({
      roleAnsteller, 
      roleRevierLeiter, 
      roleJagdleiter, 
      roleUser,
      roleHunter,
      roleRecorder,
      roleVetHDLeader,
      roleAppVet,
      roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior
    })
    const newUser: any = Object.assign({}, oldUserData ? oldUserData : {})
    // newUser.email = email
    const legalAddress = {
      address1: address1 ? address1 : undefined,
      address2: address2 ? address2 : undefined,
      zip:zip ? zip : undefined,
      city: city? city : undefined,
      country: country ? country : undefined,
      countryCode: countryCode ? countryCode : undefined
    }
    removeUndefined(legalAddress)

    newUser.firstName = firstName
    newUser.lastName = lastName
    newUser.gender = gender
    newUser.salutation = salutation
    newUser.title = title
    newUser.legalAddress = legalAddress
    newUser.signature= signature
    newUser.displayName = displayName
    newUser.roles = roles
    newUser.telephone = telephone
    newUser.contextIds = contextIds 
    newUser.contextMembership = contextMembership
    return [oldUser, newUser]
}

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.css', '../../app.component.scss']
})
export class UserFormComponent implements OnInit {

  @Input() data: UserForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() objectService: PersonService
  @Input() translateService: TranslateService

  disabledEditdata = true
  submitted = false
  // switch if form for user create
  formCreate = false
  userForm: FormGroup
  isNewUser = false;
  hide = true

  changeSignature = false
  savedSignature = false
  isSignature=false
  signImage:any;
  format="png"
  base64ImageData: any
  newDistrictSignature:string
  newSignature:string
  signPad: any;
  @ViewChild('signPadCanvas', {static: false}) signaturePadElement:any;
  ownUser=false
  changes = false
  invitationState:string
  invitationAccepted = false
  noInivited = false
  user$:any
  userId:any
  selectedContextId: any
  contextMembership = false
  contextMembershipData:any

  roleContextOptions: ContextRoleRow[] = []
  memberContexts: ContextRoleRow[] = []
  addableContexts: ContextRoleRow[] = []
  private addedContextIds = new Set<string>()
  private removedContextIds = new Set<string>()

  private refreshContextGroups(): void {
    this.memberContexts = this.roleContextOptions.filter(c => c.isMember || this.addedContextIds.has(c.id))
    this.addableContexts = this.roleContextOptions.filter(c => !c.isMember && !this.addedContextIds.has(c.id))
  }

  addRoleContext(contextId: string | null): void {
    if (!contextId) return
    this.addedContextIds.add(contextId)
    this.refreshContextGroups()
  }

  isContextIncluded(contextId: string): boolean {
    return !this.removedContextIds.has(contextId)
  }

  onContextIncludedChange(contextId: string, included: boolean): void {
    // console.log('onContextIncludedChange', contextId)
    if (included) {
      this.removedContextIds.delete(contextId)
      return
    }
    if (this.addedContextIds.has(contextId)) {
      this.addedContextIds.delete(contextId)
      this.refreshContextGroups()
      return
    }
    this.removedContextIds.add(contextId)

    // console.log('removedContextIds', this.removedContextIds)
    // console.log('addedContextIds', this.addedContextIds)
  }

  private async applyPendingContextRemovals(): Promise<void> {
    if (!this.removedContextIds.size) return
    const ids = [...this.removedContextIds]
    this.removedContextIds.clear()
    for (const contextId of ids) {
      try {
        await this.userService.removeUser(this.data.data, contextId)
        if ((this.data.data as any)?.contextMembership) delete (this.data.data as any).contextMembership[contextId]
      } catch (error) {
        console.error(error)
        this.showError(error)
      }
    }
    this.roleContextOptions = await this.computeRoleContextOptions()
    this.refreshContextGroups()
  }

  onRowRolesChange(ctx: ContextRoleRow, ids: string[] | null): void {
    let next = ids ?? []
    if (next.includes('roleRecorder') && next.length > 1) next = next.filter(id => id !== 'roleRecorder')
    ctx.roles = next
    this.selectedContextId = ctx.id

    const idSet = new Set(next)
    this.userForm.patchValue({
      roleAnsteller: idSet.has('roleAnsteller'),
      roleJagdleiter: idSet.has('roleJagdleiter'),
      roleUser: idSet.has('roleUser'),
      roleHunter: idSet.has('roleHunter'),
      roleRevierLeiter: idSet.has('roleRevierLeiter'),
      roleRecorder: idSet.has('roleRecorder'),
      roleAppVet: idSet.has('roleAppVet'),
      roleVetHDLeader: idSet.has('roleVetHDLeader'),

      roleAdmin: idSet.has('roleAdmin'), 
      roleCadaverSeek: idSet.has('roleCadaverSeek'), 
      roleCadaverBT: idSet.has('roleCadaverBT'), 
      roleStaff: idSet.has('roleStaff'), 
      roleSenior: idSet.has('roleSenior')
    })
    this.userRoles.forEach((r: any) => this.userForm.get(r.identifier)?.setValue(idSet.has(r.identifier)))

    if (ctx.id === this.data.data?.contextId) {
      this.contextMembershipData = undefined
      this.contextMembership = false
      return
    }
    const existing = (this.data.data as any)?.contextMembership?.[ctx.id]
    this.contextMembershipData = existing
      ? { ...deepCopy(existing), roles: next, identifier: ctx.id }
      : {
          contextName: ctx.name,
          email: (this.data.data as any)?.email,
          identifier: ctx.id,
          invitationState: (this.data.data as any)?.invitationState,
          roles: next,
          updateTimestamp: new Date(),
          userId: this.data.data?.identifier,
        }
    this.contextMembership = !!existing
  }

  private async computeRoleContextOptions(): Promise<ContextRoleRow[]> {
    const adminCtx = this.settingsService.selectedUserContext$?.value
    if (!adminCtx?.identifier) return []
    let subContextIds: string[] = []
    try {
      const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_CONTEXT, adminCtx.identifier))
      
      subContextIds = Array.isArray((snap.data() as any)?.subContextIds) ? (snap.data() as any).subContextIds : []
    } catch (err) {
      console.error('[UserForm] Failed to load context document for subContextIds', err)
    }

    const subIds: string[] = subContextIds.filter((id: string) => !!id && id !== adminCtx.identifier)
    const ids = [adminCtx.identifier, ...subIds]
    if (ids.length < 2) return []

    const homeId = this.data?.data?.contextId
    const membership = (this.data?.data as any)?.contextMembership ?? {}
    const membershipIds = new Set(Object.keys(membership))

    this.contextNameLookup.ensureLoaded(ids)

    const rows: ContextRoleRow[] = ids.map(id => {
      const storedName = membership[id]?.contextName
      const reliableStoredName = storedName && storedName !== id ? storedName : undefined
      return {
        id,
        name: id === adminCtx.identifier
          ? (adminCtx.contextName ?? id)
          : (reliableStoredName
            ?? this.settingsService.allUserContexts$?.value?.find((c: any) => c.identifier === id)?.contextName
            ?? this.contextNameLookup.nameOf(id)
            ?? id),
        isMember: id === homeId || membershipIds.has(id),

        roles: mapRawRolesToOptionIds(membership[id]?.roles ?? (id === homeId ? ((this.data?.data as any)?.roles ?? []) : [])),
      }
    })

    rows.forEach(row => {
      if (row.roles.length) return
      this.userService.observeUserContextMembershipOfSelectedContext(this.data.data, row.id).then(value => {
        if (!value?.roles?.length) return
        row.roles = mapRawRolesToOptionIds(value.roles)
        row.isMember = true
        if (value.contextName) row.name = value.contextName
        this.refreshContextGroups()
      }).catch(() => {})
    })

    return rows
  }

  readonly notificationEvents = NOTIFICATION_EVENTS

  rolesSelectCtrl = new FormControl<string[]>([], { nonNullable: true })
  private rolesSelectSub?: Subscription

  roleOptions: { identifier: string; title: string, disabled: boolean }[] = []

  private computeRoleOptions(): { identifier: string; title: string, disabled: boolean }[] {
    const fixed = [
      { identifier: 'roleAnsteller',    title: this.translateService.instant('FORM.userForm.roleAnsteller') , disabled: false},
      { identifier: 'roleJagdleiter',   title: this.translateService.instant('FORM.userForm.roleJagdleiter') ,disabled: false},
      { identifier: 'roleUser',         title: this.translateService.instant('FORM.userForm.roleUser') , disabled: false},
      { identifier: 'roleHunter',       title: this.translateService.instant('FORM.userForm.roleHunter') , disabled: false},
      { identifier: 'roleRevierLeiter', title: this.translateService.instant('FORM.userForm.roleRevierLeiter') , disabled: false},
      { identifier: 'roleRecorder',     title: this.translateService.instant('FORM.userForm.roleRecorder') , disabled: false},
      { identifier: 'roleAppVet',     title: this.translateService.instant('FORM.userForm.roleAppVet') , disabled: true},
      { identifier: 'roleVetHDLeader',     title: this.translateService.instant('FORM.userForm.roleVetHDLeader') , disabled: true },
      { identifier: 'roleAdmin',     title: this.translateService.instant('FORM.userForm.roleAdmin') , disabled: true },
      { identifier: 'roleCadaverSeek',     title: this.translateService.instant('FORM.userForm.roleCadaverSeek') , disabled: this.showNotificationPreferences ? false : true },
      { identifier: 'roleCadaverBT',     title: this.translateService.instant('FORM.userForm.roleCadaverBT') , disabled: this.showNotificationPreferences ? false : true },
      { identifier: 'roleStaff',     title: this.translateService.instant('FORM.userForm.roleStaff') , disabled: this.showNotificationPreferences ? false : true },
      { identifier: 'roleSenior',     title: this.translateService.instant('FORM.userForm.roleSenior') , disabled: this.showNotificationPreferences ? false : true },
    ]
    const custom = this.userRoles.map((r: any) => ({ identifier: r.identifier, title: r.title ?? r.identifier , disabled: false})) ?? []
    return [...fixed, ...custom]
  }

  roleIsSelected(id: string): boolean {
    return this.rolesSelectCtrl.value?.includes(id) ?? false;
  }

  selectedOptionRoles() {
    const ids = this.rolesSelectCtrl.value ?? [];

    return this.roleOptions.filter(r => ids.includes(r.identifier))
    .map(r => r.title).join(', ');
  }
 
  private setupRolesSelect(): void {
    this.rolesSelectSub?.unsubscribe()

    this.roleOptions = this.computeRoleOptions()
    const initial = this.roleOptions.filter(r => this.userForm.get(r.identifier)?.value === true).map(r => r.identifier)
    this.rolesSelectCtrl.setValue(initial, { emitEvent: false })
    if (this.disabledEditdata) this.rolesSelectCtrl.disable({ emitEvent: false })
    else this.rolesSelectCtrl.enable({ emitEvent: false })

    this.rolesSelectSub = this.rolesSelectCtrl.valueChanges.subscribe((selected: string[] | null) => {
      let ids = selected ?? []
      // Recorder is a fallback role — picking anything else clears it, same rule
      // the old toggles enforced via checkRecorderAvailability().
      if (ids.includes('roleRecorder') && ids.length > 1) {
        ids = ids.filter(id => id !== 'roleRecorder')
        this.rolesSelectCtrl.setValue(ids, { emitEvent: false })
      }
      const idSet = new Set(ids)
      this.roleOptions.forEach(r => {
        const ctrl = this.userForm.get(r.identifier)
        if (ctrl && !ctrl.disabled) ctrl.setValue(idSet.has(r.identifier))
      })
    })
  }

  trackByIdentifier(_index: number, item: { identifier: string }): string {
    return item.identifier
  }

  /** Same module gate as the self-service dialog. */
  get showNotificationPreferences(): boolean {
    return !!(this.settingsService.selectedUserContext$?.value as any)?.modulesMap?.get('diwima-adis')
  }

  mailControlName(event: NotificationEventDef): string { return `${event.key}_mail` }
  pushControlName(event: NotificationEventDef): string { return `${event.key}_push` }

  contextIds = []

  allCountries = []
  userRoles = []
  customRoles = false

  constructor(public dialog: MatDialog,
    public settingsService: SettingsService,
    private userService: UserService,
    private functions: AngularFireFunctions,
    private huntingDistrictService : HuntingDistrictService,
    private contextNameLookup: ContextNameLookupService,
    private firestore: Firestore,
    private userNameLookup: UserNameLookupService
  ) {
    this.contextNameLookup.names$.subscribe(names => {
      let changed = false
      this.roleContextOptions.forEach(row => {
        const resolved = names[row.id]
        if (resolved && row.name === row.id) { row.name = resolved; changed = true }
      })
      if (changed) this.refreshContextGroups()
    })
  }

  ngOnInit(): void {
    this.userService.user$.subscribe(value=> {
      const userId = this.settingsService.selectedUserContext$?.value.userId
      const contextId = this.settingsService.selectedUserContext$?.value.identifier

      if(value.roles?.includes('revierLeiter') || value.contextMembership[contextId]?.roles?.includes('revierLeiter'))
        this.disabledEditdata = false

      this.user$ = value?.displayName
      if(this.data?.data?.identifier){
        if(value.identifier == this.data.data.identifier ){
          console.log('--- editing logged user ---')
          this.userId = this.data.data.identifier
          this.ownUser=true
          this.disabledEditdata = false
          if(!value.invitationState)
            this.noInivited = true
          // else
          //   this.noInivited = false
        }
        
        this.initializeForm()
        if(this.data.data.contextMembership){
          this.observeContextMembershipOfUser()
        }else{
          if(this.userId != userId && this.data.data.contextId != contextId )
            this.observeContextMembershipOfUser()
          else if(this.userId === userId && this.data.data.contextId != contextId )
            this.observeContextMembershipOfUser()
          else
            console.log('--no contextmembership--')
        }
      }
    })

    this.selectedContextId = this.settingsService.selectedUserContext$?.value.identifier
    this.data.onNavBack = this.onCancle

    if (!this.userForm) {
      this.userForm = this.formBuilder.group({
        displayName: [, Validators.required],
        email: [, Validators.required],
        password: [, Validators.required],
        firstName:[, Validators.required],
        lastName:[, Validators.required],
        gender:[],
        salutation:[],
        title:[],
        legalAdress:[],
        address1:[],
        address2:[],
        zip:[],
        city:[],
        country:[],
        countryCode:[],
        telephone:[],
        signature:[],
        roleAnsteller: [false],
        roleJagdleiter: [false],
        roleUser: [true],
        roleHunter: [false],
        roleRevierLeiter: [false],
        roleRecorder: [false],
        roleVetHDLeader: [false],
        roleAppVet: [false],
        roleAdmin: [false], roleCadaverSeek: [false], roleCadaverBT: [false], roleStaff: [false], roleSenior: [false],
        contextMembership:[],
        contextIds:[]
      })
    }

    this.notificationEvents.forEach(event => {
      this.userForm.addControl(this.mailControlName(event), new FormControl<boolean>(false))
      this.userForm.addControl(this.pushControlName(event), new FormControl<boolean>(false))
    })

    // NAR-5647
    const context = this.settingsService.selectedUserContext$?.value
    this.customRoles = context.modulesMap?.get('customRoles') ? true : false

    // if (this.customRoles) {
      this.userRoles = context.settingsMap.get(NetworkConstants.SETTING_USER_ROLES)?.values ? (isArray(context.settingsMap.get(NetworkConstants.SETTING_USER_ROLES)?.values) ? context.settingsMap.get(NetworkConstants.SETTING_USER_ROLES)?.values : []) :  []

      this.userRoles.forEach( (role:any) => {
        const control = new FormControl<boolean>(false)
        this.userForm.addControl(role.identifier, control)
      })
    // }

    this.setupRolesSelect()

    //--------------countries
    this.allCountries = this.huntingDistrictService.allCountries$.value ? Object.values(this.huntingDistrictService.allCountries$.value) : undefined
    if(this.allCountries)
      this.allCountries.sort((a,b) => a?.name?.localeCompare(b.name));
    else
      this.huntingDistrictService.getCountriesList()

    this.huntingDistrictService.allCountries$.subscribe(value => {
      this.allCountries = value?.sort((a,b) => a?.name?.localeCompare(b.name));
    })
  }

  checkRecorderAvailability(){
    const {roleAnsteller, roleJagdleiter, roleUser, roleHunter, roleRevierLeiter} = this.userForm.value
    return (roleJagdleiter || roleUser || roleHunter || roleRevierLeiter) //? true : false
  }

  observeContextMembershipOfUser(){
    if(this.data.data?.contextMembership && this.data.data?.contextMembership[this.selectedContextId]){
      //console.log('load user.contextMembership ')
      this.loadContexMembershipData(this.data.data?.contextMembership[this.selectedContextId])
    }else{
      // for backward comp: keep looking inside COLLECTION_CONTEXT_MEMBERSHIP 
      this.userService.observeUserContextMembershipOfSelectedContext(this.data.data, this.selectedContextId).then( value => {
        //console.log('...observeUserContextMembershipOfSelectedContext...')
        if(value)
          this.loadContexMembershipData(value)
      })
    }

  }

  /** Sets both the fixed role checkboxes and every custom-role checkbox from a stored roles array — used by the plain (non-supercontext) single-context flow. */
  private patchRoleControls(roles: string[] | undefined): void {
    const ids = [...(roles ?? [])]
    const userRoles = this.determineRoles(ids)
    this.userForm.patchValue({
      roleAnsteller: userRoles.roleAnsteller,
      roleJagdleiter: userRoles.roleJagdleiter,
      roleUser: userRoles.roleUser,
      roleHunter: userRoles.roleHunter,
      roleRevierLeiter: userRoles.roleRevierLeiter,
      roleRecorder: userRoles.roleRecorder,
      roleAppVet: userRoles.roleAppVet,
      roleVetHDLeader: userRoles.roleVetHDLeader,

      roleAdmin: userRoles.roleAdmin, 
      roleCadaverSeek: userRoles.roleCadaverSeek, 
      roleCadaverBT: userRoles.roleCadaverBT, 
      roleStaff: userRoles.roleStaff, 
      roleSenior: userRoles.roleSenior
    })
    this.userRoles.forEach((role: any) => {
      this.userForm.get(role.identifier)?.setValue(ids.includes(role.identifier))
    })
    this.setupRolesSelect()
  }

  loadContexMembershipData(value){
    this.contextMembershipData = deepCopy(value)
    this.contextMembership = true
    this.patchRoleControls(value?.roles)
    this.changes = false
    if(value.invitationState){
      this.noInivited = false
      this.invitationState = value.invitationState
      if(this.invitationState == 'accepted' && this.contextMembership)
        this.invitationAccepted = true
      else
        this.invitationAccepted = false
    }else{
      this.noInivited = true
      this.invitationAccepted = false
    }
    this.userId = value?.userId
  }

  initializeForm(){
    if(this.data?.data) {
      const {displayName, email, roles, firstName,lastName, gender, salutation,title, legalAddress,
        signature, telephone, invitationState, identifier, contextMembership, contextIds } = this.data.data as FirestoreUser

      if(identifier)
        this.userId = identifier

      this.computeRoleContextOptions().then(rows => {
        this.roleContextOptions = rows
        this.refreshContextGroups()
      })

      this.invitationState = invitationState
      if(invitationState){
        if(this.data.data?.contextId == this.settingsService.selectedUserContext$?.value.identifier ){
          if(this.invitationState == 'accepted' && !this.contextMembership)
            this.invitationAccepted = true
          else
            this.invitationAccepted = false
        }
      }else{
        this.noInivited = true
        this.invitationAccepted = false
      }

      const userRoles = this.determineRoles(roles)
      this.userForm = this.formBuilder.group({
        displayName: [{value:displayName, disabled: !this.ownUser || this.disabledEditdata},  Validators.required],
        email: [{value:email, disabled:  !this.ownUser || this.disabledEditdata}, Validators.required],
        password: [{value:undefined, disabled:  !this.ownUser || this.disabledEditdata}],
        firstName:[ {value:firstName ? firstName : undefined, disabled: !this.ownUser ||this.disabledEditdata}, Validators.required],
        lastName:[ {value:lastName ? lastName : undefined, disabled: !this.ownUser ||this.disabledEditdata}, Validators.required],
        gender:[ {value:gender ? gender : undefined, disabled: !this.ownUser ||this.disabledEditdata}],
        salutation:[ {value:salutation ? salutation : undefined, disabled: !this.ownUser || this.disabledEditdata}],
        title:[{value:title ? title  : undefined, disabled: !this.ownUser || this.disabledEditdata}],
        legalAddress:[{value:legalAddress, disabled: !this.ownUser || this.disabledEditdata}],
        address1:[{value: undefined, disabled: !this.ownUser || this.disabledEditdata}],
        address2:[{value: undefined, disabled: !this.ownUser || this.disabledEditdata}],
        zip:[{value: undefined, disabled: !this.ownUser || this.disabledEditdata}],
        city:[{value: undefined, disabled: !this.ownUser || this.disabledEditdata}],
        country:[ {value: undefined, disabled: !this.ownUser || this.disabledEditdata}],
        telephone:[{value:telephone, disabled: !this.ownUser || this.disabledEditdata}],
        signature:[{value:signature, disabled: !this.ownUser || this.disabledEditdata}],
        roleAnsteller: [{value:userRoles.roleAnsteller, disabled: this.disabledEditdata}],
        roleJagdleiter: [{value:userRoles.roleJagdleiter, disabled: this.disabledEditdata}],
        roleUser: [{value:userRoles.roleUser, disabled: this.disabledEditdata}],
        roleHunter: [{value:userRoles.roleHunter, disabled: this.disabledEditdata}],
        roleRevierLeiter: [{value:userRoles.roleRevierLeiter, disabled: this.disabledEditdata}],
        roleRecorder: [{value:userRoles.roleRecorder, disabled: this.disabledEditdata || this.checkRecorderAvailability()}],
        roleAppVet: [{value:userRoles.roleAppVet, disabled: this.disabledEditdata}], 
        roleVetHDLeader: [{value:userRoles.roleVetHDLeader, disabled: this.disabledEditdata}],
        roleAdmin: [{value:userRoles.roleAdmin, disabled: this.disabledEditdata}], 
        roleCadaverSeek: [{value:userRoles.roleCadaverSeek, disabled: this.disabledEditdata}], 
        roleCadaverBT: [{value:userRoles.roleCadaverBT, disabled: this.disabledEditdata}], 
        roleStaff: [{value:userRoles.roleStaff, disabled: this.disabledEditdata}], 
        roleSenior: [{value:userRoles.roleSenior, disabled: this.disabledEditdata}],
        contextMembership: [{value:contextMembership, disabled: this.disabledEditdata }],
        contextIds: [{value: contextIds, disabled: this.disabledEditdata}],
        countryCode:[{value : undefined, disabled: this.disabledEditdata}],
      })

      const contextPrefs = resolveNotificationContextPrefs(this.data.data, this.selectedContextId)
      this.notificationEvents.forEach(event => {
        const channels: string[] = contextPrefs[event.key]?.channels ?? []
        this.userForm.addControl(this.mailControlName(event), new FormControl<boolean>(
          { value: channels.includes('email') || channels.includes('mail'), disabled: !this.ownUser || this.disabledEditdata },
        ))
        this.userForm.addControl(this.pushControlName(event), new FormControl<boolean>(
          { value: channels.includes('push'), disabled: !this.ownUser || this.disabledEditdata },
        ))
      })

      // NAR-5647
      // if (this.customRoles) {
        this.userRoles.forEach( (role:any) => {
          const usrole = roles?.includes(role.identifier)
          const control = new FormControl<boolean>(usrole)
          this.userForm.addControl(role.identifier, control)
        })
      // }

      this.setupRolesSelect()

      if(this.ownUser){
        this.userForm.get('address1').setValidators(Validators.required)
        this.userForm.get('zip').setValidators(Validators.required)
        this.userForm.get('city').setValidators(Validators.required)
        this.userForm.get('country').setValidators(Validators.required)
        this.userForm.get('countryCode').setValidators(Validators.required)
        this.userForm.get('telephone').setValidators(Validators.required)
        this.userForm.get('signature').setValidators(Validators.required)
        this.userForm.updateValueAndValidity()
      }else
      this.userForm.get('countryCode')?.disable();

      if(legalAddress){
        const { address1,address2,zip,city,country, countryCode} = legalAddress
        this.userForm.patchValue({
          address1 : address1 ? address1 : undefined,
          address2: address2 ? address2: undefined,
          zip : zip ? zip : undefined,
          city: city ? city : undefined,
          country: country ? country : undefined,
          countryCode: countryCode ? countryCode : undefined,
        })
      }

      // if(!this.contextMembership){
      //   this.userForm.patchValue({
      //     roleAnsteller: userRoles.roleAnsteller, 
      //     roleJagdleiter: userRoles.roleJagdleiter,
      //     roleUser: userRoles.roleUser, 
      //     roleHunter: userRoles.roleHunter, 
      //     roleRevierLeiter: userRoles.roleRevierLeiter, 
      //     roleRecorder: userRoles.roleRecorder,
      //   })
      // }
      this.userFeedbackService.loading.emit(false)
      this.userForm.controls.email.disable()
    } else {
      this.formCreate = true
    }

    this.userForm.get('countryCode').valueChanges.subscribe( code => {
      const country = this.allCountries?.find(c => c.isoCode === code)
      this.userForm.patchValue({country: country?.name})
    })

    this.userForm.valueChanges.subscribe( value => {
      this.changes = true
      const availability = this.checkRecorderAvailability()

      //if any other role != roleRecorder enabled --> disable recorder role
      if(availability && value.roleRecorder)
        this.userForm.patchValue({roleRecorder:false})

      //if no roles enabled --> enable recorder role
      if(!availability && this.userForm.get('roleRecorder').disabled)
        this.userForm.get('roleRecorder').enable()
    })
  }

  determineRoles(roles: string[]) {
    return {
      roleAnsteller: roles?.includes(UserRoles.ANSTELLER),
      roleJagdleiter: roles?.includes(UserRoles.JAGDLEITER),
      roleUser: roles?.includes(UserRoles.USER),
      roleHunter: roles?.includes(UserRoles.HUNTER),
      roleRevierLeiter: roles?.includes(UserRoles.REVIERLEITER),
      roleRecorder: roles?.includes(UserRoles.RECORDER),
      roleAppVet: roles?.includes(UserRoles.APPVET),
      roleVetHDLeader: roles?.includes(UserRoles.VETHDLEADER),

      roleAdmin: roles?.includes(UserRoles.ADMIN), 
      roleCadaverSeek: roles?.includes(UserRoles.CADAVERSEEK), 
      roleCadaverBT: roles?.includes(UserRoles.CADAVERBT), 
      roleStaff: roles?.includes(UserRoles.STAFF), 
      roleSenior: roles?.includes(UserRoles.SENIOR)
    }
  }

  async onSubmit() {
    this.userForm.controls.email.enable()
    if(this.newDistrictSignature)
      // this.userForm.value.signature = this.newDistrictSignature

      this.userForm.patchValue({signature: this.newDistrictSignature})
    // if (this.formCreate) {
    //   // create data 
    //   const { displayName,email, password,firstName, lastName, gender, salutation, title, address1, address2, zip, city, country, signature, telephone,
    //     roleAnsteller,roleJagdleiter, roleUser,roleHunter,roleRevierLeiter, roleRecorder } = this.userForm.value
    //   const legalAddress = { address1, address2, zip, city, country }
    //   const roles = buildRoles({roleAnsteller, roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder})
    //   const createUser: IFirestoreUser = { password, email, displayName, roles, firstName, lastName, gender, salutation, title, legalAddress, signature, telephone }
    //   try {
    //     this.userFeedbackService.loading.emit(true)
    //     await this.objectService.addUserToContext(createUser)
    //     this.userFeedbackService.loading.emit(false)
    //     const sbSuccessSave = await this.translateService.get('changes_saved').toPromise()
    //     this.userFeedbackService.showSnackBar.emit(sbSuccessSave) 
    //     this.data.onSuccessfullySubmitted()
    //   } catch (error) {
    //     console.error(error)
    //     this.userFeedbackService.loading.emit(false)
    //     this.userFeedbackService.showDialog.emit({
    //       entryComponent: GenericDialogComponent,
    //       title: "Error",
    //       message: error.error.message ? error.error.message : JSON.stringify(error),
    //     })
    //   }
      
    // } else
      // this.submitData()
      this.submitDataUser()
  }

  onCancle = async () => {
    if (this.formCreate) this.data.onCancle()
    else {
      const [oldUser, newUser] = buildUser(this.data.data, this.userForm.value)
      const diff = buildCustomDiff(oldUser, newUser)
      if (Object.keys(diff).length == 0) this.data.onCancle()
      else {
        if(this.changes){
          const title = this.translateService.instant('title_unsaved_changes')
          const message = this.translateService.instant('msg_unsaved_changes')
          this.userFeedbackService.showDialog.emit(
            {
              entryComponent: GenericDialogComponent,
              title,
              message,
              cancleCallback: async () => {
                const sbChangesDismissed = this.translateService.instant('changes_dismissed')
                this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                this.data.onCancle()
              },
              positiveCallback: async ()=> {
                if(this.data.data?.contextId === this.selectedContextId )
                  await this.onSubmit()
                else
                await this.submitData()
                this.data.onSuccessfullySubmitted()
              }
            }
          )
        }else{
          this.data.onCancle()
        }
      }
    }
  }

  onDelete(){
    if(!this.formCreate ){//this.isNewUser
      const title = this.translateService.instant('delete_title')
      const message = this.translateService.instant('delete_message')
      this.userFeedbackService.loading.emit(false)
      this.userFeedbackService.showDialog.emit({
        entryComponent: GenericDialogComponent,
        title: title,
        message: message,
        cancleCallback:()=>{
          const sbCancelDelete = this.translateService.instant('delete_canceled')
          this.userFeedbackService.showSnackBar.emit(sbCancelDelete)
        },
        positiveCallback: ()=>{

          // this.userService.deleteUser( this.data.data)
          const a = this.userService.removeUser(this.data.data, this.selectedContextId).then( async value => {
            if(value?.state == 'user-deleted' || value?.state == 'contextMembership-deleted' || value?.state == 'contextId-removed'){
              const sbDeleteCompleted = this.translateService.instant('delete_completed')
              this.userFeedbackService.showSnackBar.emit(sbDeleteCompleted)
              this.userNameLookup.refreshAllUsers();
              this.data.onCancle()
            }
          })  
        }

      })
    }else{
      this.userFeedbackService.loading.emit(true)
      const sbSNoDelete = this.translateService.instant('delete_not_possible') 
      this.userFeedbackService.showSnackBar.emit(sbSNoDelete)   
    }
  }

  ////////////////////////
  // Signature-pad functions
  openSignaturePad(){
    const dialogRef=this.dialog.open( SignaturePadDialogComponent, {
      data: {
        title:"SiganturePad",
        message:"",
        dataValues: this.userForm.value,
        className: this.data.className
      }
    })

    dialogRef.afterClosed().subscribe( result => {
      if(result[0]){
        this.newDistrictSignature=result[1]
        this.userForm.patchValue({signature: result[1]})
      }  
    })
  }

  async changeSignaturePad(){
    this.changeSignature=true
    this.savedSignature=false
    this.isSignature=false
    this.openSignaturePad()
  }

  deleteSignature(){
    this.userForm.patchValue({signature: undefined})
  }

  async sendInvitationAgain(){
    const {email,language,roles} = this.data?.data
    const userRoles = this.determineRoles(roles)

    let {roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, 
      roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior} = userRoles

    const rolesNew = buildRoles({roleAnsteller, roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior})
    const createUser: IFirestoreInviteUser = { email, language, roles: rolesNew }

    try {
      this.userFeedbackService.loading.emit(true)

      let data = {
        contextId: this.settingsService.selectedUserContext$.value?.identifier,//: string,
        userName: this.user$,//: string // Name of user who invites
        //user: createUser,//: RegisterUser
        userId: this.userId
      }


      const callable = this.functions.httpsCallable('admin-resendInviteLink')
      const reply: any = await callable(data).toPromise().then(async value => {
        if(value){
          this.userFeedbackService.loading.emit(false)
          const sbSuccessSave = this.translateService.instant('changes_saved')
          this.userFeedbackService.showSnackBar.emit(sbSuccessSave) 
          this.data.onSuccessfullySubmitted()
        }else{
          this.userFeedbackService.loading.emit(false)
        }
      })
    } catch (error) {
      console.error(error)
      this.showError(error)
    }
  }

  async updateUser(){
    const { roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior } = this.userForm.value

    let {  displayName,email,password,roles,firstName,lastName,gender,salutation,title,address1,address2,
      zip,city,country,signature,telephone, contextMembership, contextIds, countryCode } = this.ownUser ? this.userForm.value : this.data.data

      const newUserData = {
        displayName,email,password,
        roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior,
        firstName,lastName,gender,salutation,title,address1,address2, zip,city,country,signature,telephone, contextMembership, contextIds, countryCode }

      const [oldUser, newUser] = buildUser(this.data.data,newUserData)
     this.callUpdateUserFirebase(oldUser, newUser)
  }

  async updateUserDataNoRoles(){
    //console.log('...updateUserDataNoRoles...')
    const userRoles = this.determineRoles(this.data.data?.roles) // is this going to change with new user.contextMembership??
    let { roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior } = userRoles

    let {  displayName,email,password,firstName,lastName,gender,salutation,title,address1,address2,
      zip,city,country, countryCode, signature,telephone, contextIds, contextMembership } = this.userForm.value

      const newUserData = {
        displayName,email,password,
        roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior,
        firstName,lastName,gender,salutation,title,address1,address2, zip,city,country,signature,telephone, contextIds, countryCode, contextMembership }

      const [oldUser, newUser] = buildUser(this.data.data,newUserData)
     this.callUpdateUserFirebase(oldUser, newUser)
  }

  async callUpdateUserFirebase(oldUser, newUser){
    try {
      this.userFeedbackService.loading.emit(true)
      await this.objectService.updateExistingUser(oldUser, newUser)
      this.userFeedbackService.loading.emit(false)
      const sbSuccessSave = this.translateService.instant('changes_saved')
      this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
      this.data.onSuccessfullySubmitted()
    } catch (error) {
      console.error(error)
      this.showError(error)
    }
  }

  prepareContextMembershipDataToSave(data){
    const { contextName, email, identifier, invitationState, roles, updateTimestamp, userId } = this.contextMembershipData
    return { contextName, email, identifier, invitationState, roles, updateTimestamp, userId }
  }

  async updateContextMembership(){
    try{
      this.userFeedbackService.loading.emit(true)
      const { roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior} = this.userForm.value
      const newContextMembership = deepCopy(this.contextMembershipData)//this.data.data
      const roles = buildRoles({roleAnsteller, roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior})
      // save custom roles too
      let customUserRoles = this.userRoles.filter(r => this.userForm.get(r.identifier)?.value === true).map( r=> r.identifier)
      const allRoles = [...roles, ...customUserRoles]
      
      //update user.userId.contextMembership.contextId
      // newContextMembership.roles = roles
      const [oldUserCtx, newUserCtx] = buildUser(this.contextMembershipData,newContextMembership)
      newUserCtx.roles = allRoles
      // console.log('-- updateContextMembership User.id.CONTEXT_MEMBERSHIP --', oldUserCtx, newUserCtx)
      await this.objectService.updateExistingUserContextMembership(oldUserCtx, newUserCtx, this.selectedContextId, this.data.data.identifier) /// update old coll context_Membership
      this.data.onSuccessfullySubmitted()
      this.userFeedbackService.loading.emit(false)
    }catch(error){
      this.showError(error)
    }
  }

  updateUserContextMembership(){
    const { roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior} = this.userForm.value
    const roles = buildRoles({roleAnsteller, roleJagdleiter, roleUser, roleHunter, roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior})

    //update user.contextMembership
    const contextMembership = this.userForm.get('contextMembership').value ?? {}
    for (const removedId of this.removedContextIds) delete contextMembership[removedId]

    const newContextMembershipData = this.prepareContextMembershipDataToSave(this.contextMembershipData)
    newContextMembershipData.roles = roles
    if (!this.removedContextIds.has(this.selectedContextId)) {
      contextMembership[this.selectedContextId] = newContextMembershipData
    }

    let newUserData = deepCopy(this.data.data)

    if(this.ownUser){ // if ownUser, update also all other data
      let {  displayName,email,password,firstName,lastName,gender,salutation,title,address1,address2,
        zip,city,country,signature,telephone, contextIds, contextMembership } = this.userForm.value
  
        newUserData = {
          displayName,email,password,
          roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder,
          roleVetHDLeader, roleAppVet,roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior,
          firstName,lastName,gender,salutation,title,address1,address2, zip,city,country,signature,telephone, contextIds, contextMembership }
    }
    newUserData.contextMembership = contextMembership
    if(!newUserData?.contextIds?.includes(this.selectedContextId) && !this.removedContextIds.has(this.selectedContextId))
      newUserData.contextIds = [...(newUserData.contextIds ?? []), this.selectedContextId]

    const ids = Object.keys(contextMembership)
    if(newUserData?.contextIds?.length != ids?.length) {
      newUserData.contextIds = ids
    }
    const [oldUser, newUser] = buildUser(this.data.data,newUserData)
    // console.log('go to callUpdateUserFirebase --> ', oldUser, newUser)
    this.callUpdateUserFirebase(oldUser, newUser)
  }

  async submitData(){
    if (this.data.data?.contextId === this.selectedContextId ) {
      this.updateUser()
    } else {
      //update roles on contextMembership
      this.updateContextMembership() // for bacward comp: keep updatng COLECTION_CONTEXT_MEMBERSHIP

      //if its ownUser --> update also all other values on userdoc
      if(this.userId == this.settingsService.selectedUserContext$?.value.userId ) // && this.data.data.contextId != this.settingsService.selectedUserContext$?.value.identifier
        this.updateUserDataNoRoles()
    }
    //alwas update user.contextMembership
    this.updateUserContextMembership()
    // Context memberships unchecked in the "Kontext-Rollen" section only get removed for real now.
    await this.applyPendingContextRemovals()
  }

  submitDataUser(){
   
    // console.log('----submitDataUser----', this.data.data?.contextId != this.selectedContextId, this.data.data?.contextId, this.selectedContextId)
    if(this.data.data?.contextId != this.selectedContextId ) //update roles on contextMembership
      this.updateContextMembership() // for bacward comp: keep updatng COLECTION_CONTEXT_MEMBERSHIP

    let newUserData = deepCopy(this.data.data)
    const { roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior } = this.userForm.value

    // NAR-5647
    let customUserRoles = this.userRoles.filter(r => this.userForm.get(r.identifier)?.value === true).map( r=> r.identifier)
    // if ownUser --> update all with form data
    // if !ownUser --> update roles with formData
    let {  displayName,email,password,roles,firstName,lastName,gender,salutation,title,address1,address2,
      zip,city,country,signature,telephone, contextIds, countryCode } = this.ownUser ? this.userForm.value : this.data.data

    const newRoles = buildRoles({ roleAnsteller, roleRevierLeiter, roleJagdleiter, roleUser, roleHunter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior })
    const contextMembership =  deepCopy(this.data.data?.contextMembership) ?? {} // this.userForm.get('contextMembership').value ??
    for (const removedId of this.removedContextIds) delete contextMembership[removedId]
    // NAR-5647
    const allRoles = [...newRoles, ...customUserRoles]
    // const allRoles = newRoles
    if(this.ownUser) {
      const newUsData = { displayName,email,password,firstName,lastName,gender,salutation,title,address1,address2, zip,city,country,signature,telephone, contextMembership, contextIds,
        roleAnsteller,roleJagdleiter,roleUser,roleHunter,roleRevierLeiter, roleRecorder, roleVetHDLeader, roleAppVet, roleAdmin, roleCadaverSeek, roleCadaverBT, roleStaff, roleSenior,countryCode }

      const [oldUser, newUser] = buildUser(this.data.data,newUsData)
      newUserData = newUser

      const legalAddress = {
        address1: address1 ? address1 : undefined,
        address2: address2 ? address2 : undefined,
        zip:zip ? zip : undefined,
        city: city? city : undefined,
        country: country ? country : undefined,
        countryCode: countryCode ? countryCode : undefined
      }
  
      newUserData.legalAddress = legalAddress
    }

    if(this.data.data?.contextId === this.selectedContextId )
    newUserData.roles = allRoles
    else
    newUserData.roles = this.data.data?.roles

    const newContextMembershipData = this.prepareContextMembershipDataToSave(this.contextMembershipData)
    newContextMembershipData.roles = allRoles
    newContextMembershipData.updateTimestamp = new Date()
    if (!this.removedContextIds.has(this.selectedContextId)) {
      contextMembership[this.selectedContextId] = newContextMembershipData
    }

    newUserData.contextMembership = contextMembership

    if(!newUserData?.contextIds?.includes(this.selectedContextId) && !this.removedContextIds.has(this.selectedContextId))
      newUserData.contextIds = [...(newUserData.contextIds ?? []), this.selectedContextId]
    
    const ids = Object.keys(contextMembership)
    if(newUserData?.contextIds?.length != ids?.length) {
      newUserData.contextIds = ids
    }

    const [oldUser, newUser] = buildUser(this.data.data,newUserData)
    // delete this.data.data.contextMembership

    if (this.ownUser && this.showNotificationPreferences) {
      newUserData.pushNotifications = this.buildPushNotificationsPatch()
    }

    // console.log('oldUser',this.data.data, 'newUserDataUser',newUserData)
    this.callUpdateUserFirebase(this.data.data,newUserData)
    // Context memberships unchecked in the "Kontext-Rollen" section only get removed for real now.
    this.applyPendingContextRemovals()
  }


  private buildPushNotificationsPatch(): Record<string, any> | undefined {
    if (!this.selectedContextId) return (this.data.data as any)?.pushNotifications

    const contextPrefs: Record<string, { channels: string[] }> = {}
    this.notificationEvents.forEach(event => {
      const channels: string[] = []
      if (this.userForm.get(this.mailControlName(event))?.value) channels.push('email')
      if (this.userForm.get(this.pushControlName(event))?.value) channels.push('push')
      if (channels.length) contextPrefs[event.key] = { channels }
    })

    const merged: Record<string, any> = { ...((this.data.data as any)?.pushNotifications ?? {}) }
    if (Object.keys(contextPrefs).length) merged[this.selectedContextId] = contextPrefs
    else delete merged[this.selectedContextId]
    return merged
  }

  showError(error){
    this.userFeedbackService.loading.emit(false)
    this.userFeedbackService.showDialog.emit(
      {
        entryComponent: GenericDialogComponent,
        title: "Error",
        message: error?.error?.message ? error.error.message : JSON.stringify(error),
      }
    )
  }

}